import { useEffect, useState, useMemo } from 'react';
import { Product } from '../../types/product';
import {
  ActiveRecommendationWithProducts,
  getActiveRecommendationsWithProducts,
} from '../../api/getRecomnedations';
import EmblaCarousel from './recommendations-carousel';
import { Config } from '../../types/config';
import { getOrCreateSessionId } from '../../utils/get-or-create-session';
import { EmblaOptionsType } from 'embla-carousel';

const OPTIONS: EmblaOptionsType = {
  align: 'start',
  direction: 'rtl',
};

const RecommendationsPageMapper = {
  home: 'HOME_PAGE',
  product: 'PRODUCT_DETAILS_PAGE',
  cart: 'CART_PAGE',
} as const;

type RecommendationShape = ActiveRecommendationWithProducts['recommendation'];

type Props = {
  store: string;
  page: keyof typeof RecommendationsPageMapper;
  customer: string | undefined | null;
  provider: string;
  product: string;
  preview?: boolean;
  config?: Config | null;
  currency?: string;
  recommendationId?: string;
  /** New: preloaded data coming from the script as JSON attribute (string) or real object */
  preloadedProducts?: Product[] | string;
  preloadedRecommendation?: RecommendationShape | string;
  language?: string;
};

function parseMaybeJson<T>(val?: T | string | null): T | undefined {
  if (val == null) return undefined;
  if (typeof val === 'string') {
    try {
      return JSON.parse(val) as T;
    } catch {
      // If it isn't valid JSON, ignore and let network run.
      return undefined;
    }
  }
  return val as unknown as T;
}

const FinderRecomnedations = ({
  store,
  page,
  customer,
  provider,
  product,
  preview = false,
  config = null,
  currency = 'SAR',
  recommendationId,
  preloadedProducts,
  preloadedRecommendation,
  language = 'ar',
}: Props) => {
  const [
    activeRecommendationsWithProducts,
    setActiveRecommendationsWithProducts,
  ] = useState<ActiveRecommendationWithProducts[]>([]);

  const storeType = provider;

  // Normalize any preloaded values (attributes arrive as strings on the web component)
  const preloadedProductsNorm = useMemo(
    () => parseMaybeJson<Product[]>(preloadedProducts),
    [preloadedProducts],
  );
  const preloadedRecommendationNorm = useMemo(
    () => parseMaybeJson<RecommendationShape>(preloadedRecommendation),
    [preloadedRecommendation],
  );

  const seedFromPreloaded = () => {
    // Case: the script passed one recommendation's products + its meta
    if (
      preloadedProductsNorm &&
      preloadedProductsNorm.length &&
      preloadedRecommendationNorm
    ) {
      setActiveRecommendationsWithProducts([
        {
          products: preloadedProductsNorm,
          recommendation: preloadedRecommendationNorm,
        },
      ]);
      return true;
    }

    return false;
  };

  const getRecommendationProducts = async () => {
    const sessionId = getOrCreateSessionId();

    const products = await getActiveRecommendationsWithProducts({
      storeId: store,
      storeType,
      customerId:
        customer === undefined || customer === 'undefined' || customer === null
          ? sessionId
          : customer,
      pageType: RecommendationsPageMapper[page],
      productId: product,
      currency,
    });
    setActiveRecommendationsWithProducts(products);
  };

  const populatePreviewProducts = (count: number) => {
    const mockProduct: Product = {
      id: 1,
      name: { ar: 'قميص', en: 'Shirt' },
      currency: 'SAR',
      price: 1000,
      mainImage:
        'https://cdn.pixabay.com/photo/2024/04/17/18/40/ai-generated-8702726_1280.jpg',
      productId: 1,
      categories: [{ name: { ar: 'ملابس', en: 'Clothing' } }],
      is_available: true,
      media: [
        {
          type: 'image',
          url: 'https://cdn.pixabay.com/photo/2024/04/17/18/40/ai-generated-8702726_1280.jpg',
        },
      ],
      sku: 'SHIRT-001',
      tags: [{ name: { ar: 'قطني', en: 'Cotton' } }],
      type: 'product',
      parent_id: 1,
      badges: { ar: 'جديد', icon: 'new-badge.svg' },
      description: {
        ar: 'قميص قطني عالي الجودة',
        en: 'High quality cotton shirt',
      },
      promotion_title: { ar: 'خصم خاص', en: 'Special Discount' },
      quantity: 50,
      status: 'active',
      store: {
        description: 'Fashion Store',
        id: 1,
        name: 'Fashion Hub',
        route_key: 'fashion-hub',
        store_id: 'store_001',
      },
      store_id: 1,
      subtitle: { ar: 'للرجال والنساء', en: 'For Men and Women' },
      unlimited_quantity: false,
      url: '/products/shirt-001',
      weight: 0.3,
      weight_type: 'kg',
      discountedPrice: 800,
      isAvailable: true,
    };

    const defaultActiveRecommendationWithProducts: ActiveRecommendationWithProducts =
      {
        products: [mockProduct],
        recommendation: {
          id: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          pageType: 'HOME_PAGE' as const,
          type: 'trending',
          title: 'Recommended Products',
          titleEn: 'Recommended Products',
          titlePosition: 'CENTERED' as const,
          numberOfProducts: 10,
          isActive: true,
          storeId: 1,
          uiStyles: {},
        },
      };
    const repeatedProducts = Array.from(
      { length: count },
      () => defaultActiveRecommendationWithProducts,
    );
    setActiveRecommendationsWithProducts(repeatedProducts);
  };

  // const createRecommendationView = async () => {
  //   if (settings && recommendationProducts.length) {
  //     const sessionId = getSessionId();

  //     await addRecommendationView({
  //       storeId: store,
  //       sessionId,
  //       products: recommendationProducts,
  //       storeType,
  //       customerId:
  //         customer === undefined || customer === 'undefined'
  //           ? undefined
  //           : customer,
  //       pageType: RecommendationsPageMapper[page],
  //       recommendationType: settings?.type,
  //     });
  //   }
  // };

  // useEffect(() => {
  //   if (!preview) {
  //     void createRecommendationView();
  //   }
  // }, [settings, recommendationProducts.length]);

  useEffect(() => {
    // 1) If preview mode, show mock items.
    if (preview) {
      populatePreviewProducts(
        config?.recommendationsStyles?.recommendationProductsCount
          ? +config.recommendationsStyles.recommendationProductsCount
          : 5,
      );
      return;
    }

    // 2) Try to seed from preloaded props (no network)
    const seeded = seedFromPreloaded();
    if (seeded) return;

    // 3) Fallback to API fetch only when no preloaded data was passed
    void getRecommendationProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter and extract products based on recommendationId
  const filteredRecommendations = recommendationId
    ? activeRecommendationsWithProducts.filter(
        (item) => String(item.recommendation.id) === String(recommendationId),
      )
    : activeRecommendationsWithProducts;

  const recommendationProducts = filteredRecommendations.flatMap(
    (item) => item.products,
  );

  // Get the specific recommendation settings if filtering by ID
  const currentRecommendation =
    filteredRecommendations.length > 0
      ? filteredRecommendations[0].recommendation
      : null;

  // Use recommendation-specific settings if available, otherwise fall back to general settings
  const effectiveSettings = currentRecommendation;

  return (
    <>
      {Boolean(recommendationProducts?.length) && effectiveSettings && (
        <>
          <EmblaCarousel
            storeId={store}
            options={OPTIONS}
            products={recommendationProducts}
            settings={effectiveSettings}
            provider={provider}
            preview={preview}
            config={config || undefined}
            customer={customer || ''}
            currency={currency}
            language={language}
          />
        </>
      )}
    </>
  );
};

export default FinderRecomnedations;
